import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { WhatsAppIcon } from '@components/icons/WhatsAppIcon';
import { Button } from '@components/ui/Button';
import { Container } from '@components/ui/Container';
import { landingCopy } from '@features/landing/data/landing.pt';
import { trackWhatsAppClick } from '@lib/analytics';
import { cn } from '@lib/cn';
import { whatsappLink } from '@lib/whatsappLink';

const NAVBAR_SOURCE_ID = 'navbar-cta';
const SCROLL_THRESHOLD_PX = 80;
const TOP_HREF = '#top';

/**
 * Navbar fixo no topo da landing (LAYOUT-01, LAYOUT-02).
 *
 * Estados visuais:
 *  - At rest (scroll <= SCROLL_THRESHOLD_PX): fundo transparente, sem sombra
 *    — deixa o Hero respirar o `bg-neutral-50`.
 *  - On scroll (scroll > SCROLL_THRESHOLD_PX): fundo `bg-white/90` com
 *    `backdrop-blur` + `shadow-sm` (LAYOUT-02 — transicao visual sutil).
 *
 * Layout responsivo:
 *  - Mobile (< md): logo `compact` esquerda, hamburger toggle no centro-direita
 *    revelando menu vertical embaixo, CTA primario sempre visivel a direita.
 *  - Desktop (>= md): logo `full` esquerda, menu inline a direita do logo,
 *    CTA primario na extrema direita.
 *
 * Acessibilidade:
 *  - <nav aria-label="Navegação principal">
 *  - Hamburger toggle expoe `aria-expanded` + `aria-controls`.
 *  - Menu de ancoras usa `<a href="#...">` (PRD §4.2) com classes de
 *    focus-visible reutilizando o pattern do Button (`ring-brand-500`).
 *  - Logo clica para `#top` (volta ao Hero) com aria-label.
 *
 * CONV-03: o CTA primario passa `sourceId='navbar-cta'` no whatsappLink — origem
 * distinta do Hero, do FAB e do CTA Final (Phase 5).
 */
export const Navbar = (): JSX.Element => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = (): void => {
      setIsScrolled(window.scrollY > SCROLL_THRESHOLD_PX);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const whatsappHref = whatsappLink(landingCopy.whatsapp.defaultMessage, NAVBAR_SOURCE_ID);
  const { navbar } = landingCopy;

  return (
    <nav
      aria-label="Navegação principal"
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-200',
        isScrolled
          ? 'bg-white/90 backdrop-blur-md shadow-sm'
          : 'bg-transparent backdrop-blur-0 shadow-none',
      )}
    >
      <Container className="flex h-16 items-center justify-between gap-4 md:h-20">
        <a
          href={TOP_HREF}
          aria-label={navbar.logoAriaLabel}
          className="rounded-md text-brand-700 outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-50"
        >
          <span className="inline-flex items-center ">
            <img
              src="/omanna_icon.svg"
              alt=""
              aria-hidden="true"
              decoding="async"
              loading="eager"
              className="block h-13 w-13 shrink-0"
            />
            <span className="inline-flex flex-col leading-none">
              <span className="font-display text-2xl font-bold tracking-tight text-neutral-900">
                omanna
              </span>
              <span className="mt-0.5 hidden font-sans text-[0.6rem] font-medium uppercase tracking-[0.18em] text-brand-700 md:block">
                Provisão em Software
              </span>
            </span>
          </span>
        </a>

        <div className="hidden flex-1 items-center justify-center gap-8 md:flex">
          {navbar.menuItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="rounded-sm text-base font-medium text-neutral-700 outline-none transition-colors hover:text-brand-600 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-50"
            >
              {item.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="md"
            as="a"
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={navbar.ctaAriaLabel}
            icon={<WhatsAppIcon className="h-5 w-5" />}
            className="hidden sm:inline-flex"
            onClick={() => {
              trackWhatsAppClick(NAVBAR_SOURCE_ID);
            }}
          >
            {navbar.ctaLabel}
          </Button>

          <button
            type="button"
            aria-label={navbar.menuToggleAriaLabel}
            aria-expanded={isMenuOpen}
            aria-controls="navbar-mobile-menu"
            onClick={() => {
              setIsMenuOpen((open) => !open);
            }}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-neutral-700 outline-none transition-colors hover:bg-neutral-100 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-50 md:hidden"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable={false}
            >
              {isMenuOpen ? (
                <path d="M18 6 6 18M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </Container>

      {isMenuOpen && (
        <div
          id="navbar-mobile-menu"
          className="border-t border-neutral-200 bg-white/95 backdrop-blur-md md:hidden"
        >
          <Container className="flex flex-col gap-1 py-4">
            {navbar.menuItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => {
                  setIsMenuOpen(false);
                }}
                className="rounded-md px-3 py-3 text-base font-medium text-neutral-700 outline-none transition-colors hover:bg-neutral-100 hover:text-brand-700 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-50"
              >
                {item.label}
              </a>
            ))}
            <Button
              variant="primary"
              size="md"
              as="a"
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={navbar.ctaAriaLabel}
              icon={<WhatsAppIcon className="h-5 w-5" />}
              className="mt-2 sm:hidden"
              onClick={() => {
                trackWhatsAppClick(NAVBAR_SOURCE_ID);
                setIsMenuOpen(false);
              }}
            >
              {navbar.ctaLabel}
            </Button>
          </Container>
        </div>
      )}
    </nav>
  );
};
